import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDelete, apiGet, apiPatch, apiPost, errOf } from '../../lib/api';
import MemberForm, { type MemberFormData, type TypeRow } from '../../components/MemberForm';
import InviteMemberButton from '../../components/InviteMemberButton';

interface MemberRow {
  id: string;
  member_no: string | null;
  type_code: string;
  name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  user_id?: string | null;
  notes: string | null;
}

const th: CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-divider)', fontSize: 'var(--font-size-tiny)' };
const td: CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--color-divider)', fontSize: 'var(--font-size-small)', verticalAlign: 'top' };
const btn: CSSProperties = { padding: '4px 10px', borderRadius: 8, border: '1px solid var(--color-divider)', background: 'var(--color-card)', fontSize: 'var(--font-size-tiny)', cursor: 'pointer' };
const btnPrimary: CSSProperties = { ...btn, background: 'var(--color-primary)', color: 'var(--color-on-primary)' };

const blank: MemberFormData = { type_code: 'individual', name: '', company: '', email: '', phone: '', status: 'active', notes: '' };

/** 會員管理（列表 ＋ 新增／編輯／刪除 ＋ 發邀請）—— 只限職員 */
export default function AdminMembersPage() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [types, setTypes] = useState<TypeRow[]>([]);
  const [form, setForm] = useState<MemberFormData | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const [m, ty] = await Promise.all([
      apiGet<{ members: MemberRow[] }>('/api/members'),
      apiGet<{ types: TypeRow[] }>('/api/member-types'),
    ]);
    if (m.ok && m.data) setRows(m.data.members);
    if (ty.ok && ty.data) setTypes(ty.data.types);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const typeName = (code: string): string => {
    const ty = types.find((x) => x.code === code);
    if (!ty) return code;
    if (i18n.language === 'en') return ty.name_en;
    if (i18n.language === 'zh-CN') return ty.name_zh_cn;
    return ty.name_zh_hk;
  };

  async function save() {
    if (!form) return;
    setErr('');
    const r = editId ? await apiPatch(`/api/members/${editId}`, form) : await apiPost('/api/members', form);
    if (!r.ok) {
      setErr(errOf(r.data) || '儲存失敗');
      return;
    }
    setForm(null);
    setEditId(null);
    await load();
  }

  async function remove(id: string) {
    if (!window.confirm(t('confirm_delete'))) return;
    await apiDelete(`/api/members/${id}`);
    await load();
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold">{t('admin_members')}</h2>
        <button
          type="button"
          style={btnPrimary}
          onClick={() => {
            setEditId(null);
            setForm({ ...blank });
            setErr('');
          }}
        >
          ＋ {t('btn_new')}
        </button>
      </div>
      {form && (
        <MemberForm
          form={form}
          setForm={setForm}
          types={types}
          typeName={typeName}
          err={err}
          onSave={() => void save()}
          onCancel={() => {
            setForm(null);
            setEditId(null);
          }}
        />
      )}

      <div style={{ background: 'var(--color-card)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>{t('col_no')}</th>
              <th style={th}>{t('col_name')}</th>
              <th style={th}>{t('col_type')}</th>
              <th style={th}>{t('col_status')}</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td style={td} colSpan={5}>
                  {t('empty_none')}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={td}>{r.member_no ?? '—'}</td>
                <td style={td}>
                  {r.name}
                  {r.company ? (
                    <div style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--color-text-secondary)' }}>{r.company}</div>
                  ) : null}
                  {r.user_id ? (
                    <div style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--color-primary)' }}>✓ {t('invite_bound')}</div>
                  ) : null}
                </td>
                <td style={td}>{typeName(r.type_code)}</td>
                <td style={td}>{t(`status_${r.status}`)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    style={btn}
                    onClick={() => {
                      setEditId(r.id);
                      setForm({
                        type_code: r.type_code,
                        name: r.name,
                        company: r.company ?? '',
                        email: r.email ?? '',
                        phone: r.phone ?? '',
                        status: r.status,
                        notes: r.notes ?? '',
                      });
                      setErr('');
                    }}
                  >
                    {t('btn_edit')}
                  </button>{' '}
                  <InviteMemberButton memberId={r.id} email={r.email} />{' '}
                  <button type="button" style={btn} onClick={() => void remove(r.id)}>
                    {t('btn_delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}
